<script lang="ts">
    import type { BlogPost } from '../../api/blogs.ts';
    import { noPictureUrl } from '../../api/common.ts';
    import { push } from 'svelte-spa-router';
    import CaretRightIcon from '../../icons/CaretRightIcon.svelte';

    export let post: BlogPost;

    function goToBlogPost() {
        push(`/Blog/${post.id}`);
    }

    function handleKeydown(e: KeyboardEvent) {
        if (e.currentTarget !== e.target) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            goToBlogPost();
        }
    }
</script>

<div
    role="link"
    tabindex="0"
    on:click={goToBlogPost}
    on:keydown={handleKeydown}
    class="group flex flex-col md:flex-row border border-gray-300 border-l border-l-gray-300 bg-white overflow-hidden h-auto rounded-lg transition-[transform,box-shadow,border-color,border-width] duration-300 ease-in-out transform-gpu origin-left will-change-transform hover:scale-[1.005] hover:shadow-lg hover:border-l-4 hover:border-l-[var(--vv-action-center-button-color,#0d9488)] active:border-l-4 active:border-l-[var(--vv-action-center-button-color,#0d9488)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vv-action-center-button-color,#0d9488)] focus-visible:ring-offset-2 active:outline-none cursor-pointer w-full box-border {post.imageUrl
        ? 'md:h-[280px]'
        : 'md:h-[250px]'}"
    data-testid={`blog-post-${post.id}`}
    aria-label="Blog post: {post.headline}. By {post.author}, published {post.getDisplayedPublicationDate()}. Click to read."
>
        <!-- Left: client image, or headline + date on desktop (matches petition cards) -->
        <div
            class="w-full md:w-2/5 flex-shrink-0 {post.imageUrl ? '' : 'hidden md:block md:border-r md:border-gray-200'}"
        >
            {#if post.imageUrl}
                <img
                    src={post.imageUrl}
                    alt={post.headline}
                    class="w-full h-[160px] md:h-full object-cover"
                    data-testid="image"
                />
            {:else}
                <div class="p-4 md:px-5 md:pb-5 md:pt-4 flex flex-col gap-1 h-full">
                    <h2
                        class="!text-base md:!text-lg !font-semibold !text-gray-900 !leading-tight !m-0 line-clamp-3"
                        data-testid="headline"
                    >
                        {post.headline}
                    </h2>
                    <p class="!text-sm !leading-5 !text-gray-500 !m-0" data-testid="date">
                        {post.getDisplayedPublicationDate()}
                    </p>
                </div>
            {/if}
        </div>

        <div class="w-full md:w-3/5 p-4 md:p-5 flex flex-col justify-between" data-testid="content">
            <div class="flex flex-col gap-2">
                {#if !post.imageUrl}
                    <div class="md:hidden">
                        <h2
                            class="text-base font-semibold text-gray-900 leading-tight line-clamp-3"
                            data-testid="headline"
                        >
                            {post.headline}
                        </h2>
                        <p class="text-sm text-gray-500 mt-1" data-testid="date">
                            {post.getDisplayedPublicationDate()}
                        </p>
                    </div>
                {/if}

                <div class="flex items-center gap-3 flex-shrink-0">
                    <img
                        src={noPictureUrl}
                        alt=""
                        class="w-10 h-10 md:w-11 md:h-11 rounded-full flex-shrink-0 border-2 border-gray-100 object-cover"
                    />
                    <div class="flex flex-col gap-0.5 justify-center min-w-0 py-0.5">
                        <p
                            class="text-sm md:text-base font-medium text-gray-900 leading-5 truncate m-0"
                            data-testid="author"
                        >
                            {post.author}
                        </p>
                        {#if post.imageUrl}
                            <p class="text-sm text-gray-500 leading-5 m-0" data-testid="date">
                                {post.getDisplayedPublicationDate()}
                            </p>
                        {/if}
                    </div>
                </div>

                {#if post.imageUrl}
                    <h2
                        class="text-base md:text-lg font-semibold text-gray-900 leading-tight line-clamp-2 flex-shrink-0"
                        data-testid="headline"
                    >
                        {post.headline}
                    </h2>
                {/if}

                <p class="text-sm text-gray-600 leading-relaxed line-clamp-2" data-testid="shortened-alert">
                    {post.shortenedAlert}
                </p>
            </div>

            <div
                class="flex items-center gap-1 text-sm text-gray-900 font-medium pt-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            >
                <span>Read article</span>
                <CaretRightIcon color="currentColor" />
            </div>
        </div>
</div>
